import React, { useState } from "react";
import { Box, Typography, Container, Avatar, Divider, Chip } from "@mui/material";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Autoplay } from "swiper";
import "swiper/css";
import "swiper/css/navigation";

// Icons for values
import VisibilityIcon from "@mui/icons-material/Visibility";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import SecurityIcon from "@mui/icons-material/Security";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import GroupsIcon from "@mui/icons-material/Groups";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";

// Icons for "why us"
import PetsIcon from "@mui/icons-material/Pets";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ConnectWithoutContactIcon from "@mui/icons-material/ConnectWithoutContact";
import FavoriteIcon from "@mui/icons-material/Favorite";

SwiperCore.use([Autoplay, Navigation]);

/* ── Data ──────────────────────────────────────────── */
const values = [
  { icon: <VisibilityIcon sx={{ fontSize: 28, color: "#FFC001" }} />, title: "Transparency", description: "We require everyone on our platform to be transparent about animal safety and well-being. Only reputable businesses with proper documentation are allowed." },
  { icon: <SentimentSatisfiedAltIcon sx={{ fontSize: 28, color: "#FFC001" }} />, title: "Customer Satisfaction", description: "Whether finding a pet, trainer, or groomer — we're here to help. All your questions and concerns are always welcome." },
  { icon: <SecurityIcon sx={{ fontSize: 28, color: "#FFC001" }} />, title: "Safety", description: "The safety of animals and every person using our services is a top priority. Regulated standards ensure you're always in great hands." },
  { icon: <TouchAppIcon sx={{ fontSize: 28, color: "#FFC001" }} />, title: "Ease", description: "Our platform is easy to use and navigate. From instruction videos to social media, we provide all the tools you need." },
  { icon: <GroupsIcon sx={{ fontSize: 28, color: "#FFC001" }} />, title: "Community", description: "Join a vibrant community of pet enthusiasts. Share stories, seek advice, and connect with like-minded individuals who share your passion." },
  { icon: <AccessibilityNewIcon sx={{ fontSize: 28, color: "#FFC001" }} />, title: "Accessibility", description: "All information about our company and services are just a click away — pet care guides, purchase processes, and seller profiles." },
];

const whyUs = [
  { icon: <PetsIcon sx={{ fontSize: 32, color: "#FFC001" }} />, title: "We're Pet Owners", description: "As fellow pet owners, we know what it takes to care for an animal, especially an exotic one. Who better to turn to than experts like us." },
  { icon: <StorefrontIcon sx={{ fontSize: 32, color: "#FFC001" }} />, title: "We Support Businesses", description: "We make it easy and accessible for you to build and grow your business with us." },
  { icon: <ConnectWithoutContactIcon sx={{ fontSize: 32, color: "#FFC001" }} />, title: "We Create Connections", description: "With so many different people on our platform, we help create meaningful connections within our growing community." },
  { icon: <FavoriteIcon sx={{ fontSize: 32, color: "#FFC001" }} />, title: "We Care", description: "No matter what side you're on, we always have your best interest in discovering what you need from our community." },
];

const crew = [
  { image: "https://stagingappdata.s3.amazonaws.com/crew/image.png", name: "Noah Watkins", role: "CEO — Founder" },
  { image: "https://stagingappdata.s3.amazonaws.com/crew/faseeh.jpg", name: "Muhammad Faseeh", role: "Project Lead" },
  { image: "https://res.cloudinary.com/dzsz395eb/image/upload/v1694612934/profile-pic_1_k3um2z.png", name: "Hamza Soban", role: "Technical Lead" },
  { image: "https://stagingappdata.s3.amazonaws.com/crew/maaz.jpeg", name: "Maaz Shah", role: "Backend Engineer" },
  { image: "https://res.cloudinary.com/dzsz395eb/image/upload/v1694536407/WhatsApp_Image_2023-09-12_at_9.24.03_PM_rxg9pe.jpg", name: "Ayiza Asghar", role: "Frontend Engineer" },
  { image: "https://stagingappdata.s3.amazonaws.com/crew/rida.jpg", name: "Rida Ejaz", role: "Animal Researcher" },
  { image: "https://stagingappdata.s3.amazonaws.com/crew/ibrahim.jpeg", name: "Muhammad Ibrahim", role: "Lead Designer" },
  { image: "https://stagingappdata.s3.amazonaws.com/crew/adnan.jpeg", name: "M. Adnan", role: "Creative Director" },
];

const missionParagraphs = [
  "We are Moore Exotics. We're not just about transactions — we're about creating bonds that last a lifetime. We're passionate about connecting loving pet owners with reputable businesses who prioritize ethical practices and the safety of their animals.",
  "We work hard to ensure that everyone on our platform adheres to strict ethical standards. We carefully evaluate all businesses to confirm they are committed to providing animals with proper care, safety, and love.",
  "At Moore Exotics, we promote responsible ownership of pets, especially exotic ones. Our team is made up of dedicated animal lovers who understand the importance of responsible pet ownership and its impact on the lives of pets and their owners.",
  "We're dedicated to helping our customers through every step — from finding the perfect pet, to improving the lives of their current ones. Thank you for choosing us. We look forward to helping you find your new furry, scaly, or feathered friend!",
];

/* ══════════════════════════════════════════════════════ */
const AboutUs = () => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <Helmet>
        <meta property="og:image" content="https://prodappdata.s3.amazonaws.com/preview-images/about-preview.png" />
        <meta property="og:title" content="About Us" />
        <meta property="og:description" content="Moore Exotics About Us" />
      </Helmet>

      <Box sx={{ minHeight: "100vh", backgroundColor: "#111" }}>

        {/* ── Hero ── */}
        <Box sx={{ position: "relative", background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)", borderBottom: "1px solid #222", py: { xs: 6, md: 10 }, overflow: "hidden" }}>
          <Box sx={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
          {/* Decorative grid */}
          <Box sx={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,192,1,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,192,1,0.025) 1px, transparent 1px)", backgroundSize: "60px 60px", pointerEvents: "none" }} />
          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
            {/* Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 4 }}>
              <Typography component={Link} to="/" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
              <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>
              <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>About Us</Typography>
            </Box>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: "easeOut" }}>
              <Chip label="OUR STORY" size="small" sx={{ backgroundColor: "rgba(255,192,1,0.12)", color: "#FFC001", fontWeight: 700, fontSize: "11px", letterSpacing: "0.1em", border: "1px solid rgba(255,192,1,0.3)", mb: 2 }} />
              <Typography sx={{ fontWeight: 800, fontSize: { xs: "36px", sm: "52px", md: "68px" }, color: "#fff", lineHeight: 1.05, mb: 2 }}>
                About{" "}<Box component="span" sx={{ color: "#FFC001" }}>Moore</Box>
                <br />Exotics
              </Typography>
              <Typography sx={{ color: "#777", fontSize: { xs: "15px", md: "17px" }, maxWidth: "560px", lineHeight: 1.8 }}>
                Connecting passionate pet owners with reputable breeders and businesses — built on trust, ethics, and love for animals.
              </Typography>
            </motion.div>
          </Container>
        </Box>

        {/* ── Mission ── */}
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 4, md: 8 }, alignItems: "center" }}>
            {/* Text */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: "easeOut" }}>
              <Typography sx={{ color: "#FFC001", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", mb: 1.5 }}>Our Mission</Typography>
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "24px", md: "32px" }, lineHeight: 1.2, mb: 3 }}>
                Connecting your pets with the best
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {(expanded ? missionParagraphs : missionParagraphs.slice(0, 1)).map((p, i) => (
                  <Typography key={i} sx={{ color: "#888", fontSize: "15px", lineHeight: 1.9 }}>{p}</Typography>
                ))}
              </Box>
              <Box
                onClick={() => setExpanded((v) => !v)}
                sx={{ display: "inline-flex", alignItems: "center", mt: 3, cursor: "pointer", color: "#FFC001", fontSize: "14px", fontWeight: 600, borderBottom: "1px solid rgba(255,192,1,0.4)", pb: 0.2, "&:hover": { borderBottomColor: "#FFC001" } }}
              >
                {expanded ? "Show Less" : "Read Full Story"}
              </Box>
            </motion.div>

            {/* Logo / image */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, ease: "easeOut", delay: 0.1 }}>
              <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "20px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", p: 4, minHeight: 300 }}>
                <img
                  src="https://res.cloudinary.com/dwezij7u7/image/upload/v1675115795/exoticslogo_w2oi9z.png"
                  alt="Moore Exotics"
                  style={{ width: "100%", maxWidth: 340, objectFit: "contain", filter: "drop-shadow(0 0 40px rgba(255,192,1,0.15))" }}
                />
              </Box>
            </motion.div>
          </Box>
        </Container>

        <Divider sx={{ borderColor: "#1a1a1a" }} />

        {/* ── Values ── */}
        <Box sx={{ backgroundColor: "#0d0d0d", py: { xs: 6, md: 10 } }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Chip label="WHAT WE STAND FOR" size="small" sx={{ backgroundColor: "rgba(255,192,1,0.12)", color: "#FFC001", fontWeight: 700, fontSize: "11px", letterSpacing: "0.1em", border: "1px solid rgba(255,192,1,0.3)", mb: 2 }} />
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "26px", md: "38px" } }}>
                Our Core <Box component="span" sx={{ color: "#FFC001" }}>Values</Box>
              </Typography>
              <Typography sx={{ color: "#666", fontSize: "15px", mt: 1.5, maxWidth: 560, mx: "auto" }}>
                Since our values are rooted in promoting safe and ethical business practices, here's what we guarantee.
              </Typography>
            </Box>
            <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" } }}>
              {values.map((v, i) => (
                <motion.div key={v.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, ease: "easeOut", delay: (i % 3) * 0.08 }}>
                  <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3, height: "100%", transition: "all 0.25s ease", "&:hover": { borderColor: "#FFC001", boxShadow: "0 8px 24px rgba(255,192,1,0.08)", transform: "translateY(-3px)" } }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: "12px", backgroundColor: "rgba(255,192,1,0.1)", border: "1px solid rgba(255,192,1,0.2)", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                      {v.icon}
                    </Box>
                    <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "16px", mb: 1 }}>{v.title}</Typography>
                    <Typography sx={{ color: "#777", fontSize: "13px", lineHeight: 1.8 }}>{v.description}</Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </Container>
        </Box>

        <Divider sx={{ borderColor: "#1a1a1a" }} />

        {/* ── Why Us ── */}
        <Container maxWidth="xl" sx={{ py: { xs: 6, md: 10 } }}>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Chip label="GET TO KNOW US" size="small" sx={{ backgroundColor: "rgba(255,192,1,0.12)", color: "#FFC001", fontWeight: 700, fontSize: "11px", letterSpacing: "0.1em", border: "1px solid rgba(255,192,1,0.3)", mb: 2 }} />
            <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "26px", md: "38px" } }}>
              Why <Box component="span" sx={{ color: "#FFC001" }}>Choose Us</Box>
            </Typography>
          </Box>
          <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" } }}>
            {whyUs.map((w, i) => (
              <motion.div key={w.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, ease: "easeOut", delay: i * 0.08 }}>
                <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3, textAlign: "center", height: "100%", transition: "all 0.25s ease", "&:hover": { borderColor: "#FFC001", boxShadow: "0 8px 24px rgba(255,192,1,0.08)", transform: "translateY(-3px)" } }}>
                  <Box sx={{ width: 60, height: 60, borderRadius: "50%", backgroundColor: "rgba(255,192,1,0.1)", border: "1px solid rgba(255,192,1,0.2)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
                    {w.icon}
                  </Box>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px", mb: 1 }}>{w.title}</Typography>
                  <Typography sx={{ color: "#777", fontSize: "13px", lineHeight: 1.8 }}>{w.description}</Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>

        <Divider sx={{ borderColor: "#1a1a1a" }} />

        {/* ── Team ── */}
        <Box sx={{ backgroundColor: "#0d0d0d", py: { xs: 6, md: 10 } }}>
          <Container maxWidth="xl">
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Chip label="THE PEOPLE" size="small" sx={{ backgroundColor: "rgba(255,192,1,0.12)", color: "#FFC001", fontWeight: 700, fontSize: "11px", letterSpacing: "0.1em", border: "1px solid rgba(255,192,1,0.3)", mb: 2 }} />
              <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "26px", md: "38px" } }}>
                Meet the <Box component="span" sx={{ color: "#FFC001" }}>Crew</Box>
              </Typography>
              <Typography sx={{ color: "#666", fontSize: "15px", mt: 1.5 }}>
                The passionate team behind Moore Exotics
              </Typography>
            </Box>
            <Swiper
              style={{ "--swiper-navigation-color": "#FFC001" }}
              navigation
              modules={[Navigation]}
              grabCursor
              loop
              spaceBetween={20}
              breakpoints={{
                320: { slidesPerView: 1 },
                480: { slidesPerView: 2 },
                768: { slidesPerView: 3 },
                1100: { slidesPerView: 4 },
              }}
            >
              {crew.map((member) => (
                <SwiperSlide key={member.name} style={{ padding: "8px 4px 24px" }}>
                  <Box sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", borderRadius: "16px", p: 3, textAlign: "center", transition: "all 0.25s ease", "&:hover": { borderColor: "#FFC001", boxShadow: "0 8px 24px rgba(255,192,1,0.08)", transform: "translateY(-3px)" } }}>
                    <Avatar
                      src={member.image}
                      alt={member.name}
                      sx={{ width: 90, height: 90, mx: "auto", mb: 2, border: "3px solid rgba(255,192,1,0.25)", boxShadow: "0 0 20px rgba(255,192,1,0.1)" }}
                    />
                    <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px", mb: 0.3 }}>{member.name}</Typography>
                    <Typography sx={{ color: "#FFC001", fontSize: "12px", fontWeight: 600 }}>{member.role}</Typography>
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          </Container>
        </Box>

      </Box>
    </>
  );
};

export default AboutUs;
